import React, { useState, useCallback } from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import Modal from '@adsk/alloy-react-modal';

import EntityPickerModalContent from './EntityPickerModalContent';
import EntityPickerModalTopBar from './EntityPickerModalTopBar';
import { createFormPropsShape, pluginShape } from '../helpers/propTypes';
import useEntitySearch from '../hooks/useEntitySearch';

const FLOATING_PANEL_THRESHOLD = 1140;

const EntityPickerModalBody = ({
  sourceEntityType,
  plugin,
  loading: loadingProp,
  error: errorProp,
  retry: retryProp,
  userPermissionsData,
  onChange,
  setSelection,
  isSaving,
  addEntitiesToCache,
  selectedIds,
  disabledIds,
  multiple,
  createFormProps,
  dataViewProps,
  onCreateSuccess,
  onUpdate,
  createPanelOpen,
  setCreatePanelOpen,
  infoMessage,
  isPluginDisabledInEntitlements,
}) => {
  const {
    entities: searchedEntities,
    loading: loadingEntities,
    error: errorLoadingEntities,
    onTryAgain: reloadEntities,
    onLoadMore,
    filters,
    setFilters,
    sort,
    setSort,
  } = useEntitySearch({ plugin, addEntitiesToCache });

  const {
    showSearchBox,
    FiltersPanel,
    CreateNewEntityPanel,
    TopBarActions,
    modalProps,
    onSearchExpressionChange,
    CustomTopBar,
  } = plugin;

  const TopBar = CustomTopBar || EntityPickerModalTopBar;

  const width = modalProps?.width;
  const size = modalProps?.size || Modal.SIZES.EXTRA_LARGE;
  const floatingPanel = width
    ? width < FLOATING_PANEL_THRESHOLD
    : Modal.SIZES.EXTRA_LARGE !== size;

  const [filtersOpen, setFiltersOpen] = useState(false);

  const closeFiltersPanel = useCallback(() => setFiltersOpen(false), []);
  const openFiltersPanel = useCallback(() => {
    setFiltersOpen(true);
    setCreatePanelOpen(false);
  }, [setCreatePanelOpen]);
  const toggleFiltersPanel = useCallback(() => {
    setFiltersOpen((v) => !v);
    setCreatePanelOpen(false);
  }, [setCreatePanelOpen]);

  const closeCreatePanel = useCallback(
    () => setCreatePanelOpen(false),
    [setCreatePanelOpen],
  );
  const openCreatePanel = useCallback(() => {
    setCreatePanelOpen(true);
    setFiltersOpen(false);
  }, [setCreatePanelOpen]);

  const projectId = useCurrentProjectId();

  // When clearing filters we want to keep search box state.
  const ignoreFilterKeys = showSearchBox ? ['searchExpression'] : [];

  const clearFilters = () =>
    setFilters(
      ignoreFilterKeys.reduce((acc, k) => {
        acc[k] = filters[k];
        return acc;
      }, {}),
    );

  const filtersCount = Object.keys(filters).filter(
    (k) => !ignoreFilterKeys.includes(k),
  ).length;

  // We can't show entities before getting all data needed, so we could mark already linked ones.
  const entities = loadingProp || errorProp ? [] : searchedEntities;

  const loading = loadingProp || loadingEntities || userPermissionsData.loading;
  const error =
    !!errorProp || errorLoadingEntities || !!userPermissionsData.error;
  const onTryAgain = error
    ? () => {
        errorProp && retryProp && retryProp();
        userPermissionsData.error &&
          userPermissionsData.retry &&
          userPermissionsData.retry();
        errorLoadingEntities && reloadEntities();
      }
    : undefined;

  const canCreate = userPermissionsData?.canCreate;
  const renderCreatePanel = canCreate && CreateNewEntityPanel;
  const disabled = isSaving || createPanelOpen;
  const filtersAvailable = !!FiltersPanel;

  return (
    <div
      css={[
        {
          position: 'relative',
          maxHeight: '100%',
          display: 'flex',
          flex: 1,
        },
      ]}
    >
      <div
        css={[
          {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            padding: 16,
            minWidth: 0,
          },
        ]}
      >
        {(showSearchBox ||
          canCreate ||
          FiltersPanel ||
          TopBarActions ||
          CustomTopBar) && (
          <TopBar
            searchExpression={filters?.searchExpression}
            onSearchChange={(searchExpression) => {
              const newFilters = { ...filters };
              if (searchExpression) {
                newFilters.searchExpression = searchExpression;
              } else {
                delete newFilters.searchExpression;
              }
              setFilters(newFilters);

              if (onSearchExpressionChange)
                onSearchExpressionChange(searchExpression);
            }}
            showSearchBox={showSearchBox}
            canCreate={canCreate}
            onCreateClick={openCreatePanel}
            plugin={plugin}
            disabled={disabled}
            filtersAvailable={filtersAvailable}
            filtersOpen={filtersOpen}
            filters={filters}
            toggleFiltersPanel={toggleFiltersPanel}
            clearFilters={clearFilters}
            filtersCount={filtersCount}
            setFilters={setFilters}
            infoMessage={infoMessage}
            createFormProps={createFormProps}
            isPluginDisabledInEntitlements={isPluginDisabledInEntitlements}
          />
        )}
        {plugin && (
          <EntityPickerModalContent
            entities={entities}
            loading={loading}
            error={error}
            onTryAgain={onTryAgain}
            setSelection={setSelection}
            onChange={onChange}
            disabled={disabled}
            selectedIds={selectedIds}
            disabledIds={disabledIds}
            multiple={multiple}
            onLoadMore={onLoadMore}
            plugin={plugin}
            filters={filters}
            addEntitiesToCache={addEntitiesToCache}
            canCreate={canCreate}
            onCreateClick={openCreatePanel}
            setFilters={setFilters}
            openFiltersPanel={openFiltersPanel}
            dataViewProps={dataViewProps}
            onUpdate={onUpdate}
            sort={sort}
            setSort={setSort}
            isPluginDisabledInEntitlements={isPluginDisabledInEntitlements}
          />
        )}
      </div>
      {filtersAvailable && (
        <FiltersPanel
          open={filtersOpen}
          filters={filters}
          setFilters={setFilters}
          onClose={closeFiltersPanel}
          floatingPanel={floatingPanel}
          clearFilters={clearFilters}
          hasFilters={!!filtersCount}
        />
      )}
      {renderCreatePanel && (
        <CreateNewEntityPanel
          {...createFormProps}
          projectId={projectId}
          open={createPanelOpen}
          sourceEntityType={sourceEntityType}
          onClose={closeCreatePanel}
          floatingPanel={floatingPanel}
          onCreateSuccess={onCreateSuccess}
        />
      )}
    </div>
  );
};

EntityPickerModalBody.propTypes = {
  /**
   * The source entity type, as used by relationship service.
   */
  sourceEntityType: PropTypes.string,
  /**
   * The plugin to use for selected entity type.
   */
  plugin: pluginShape,
  /**
   * Whether we currently load data
   */
  loading: PropTypes.bool,
  /**
   * Whether fetching data raised an error
   */
  error: PropTypes.bool,
  /**
   * Retry loading data in case of an error. Required when `error` is true
   */
  retry: PropTypes.func,
  /**
   * The date related to user permissions fetch.
   */
  userPermissionsData: PropTypes.shape({
    /** A boolean represents whether current user can create entity for given plugin  */
    canCreate: PropTypes.bool,
    /** Whether we currently load user permissions request */
    loading: PropTypes.bool,
    /** Whether fetching user permissions raised an error */
    error: PropTypes.bool,
    /** Retry loading user permissions in case of an error. Required when `userPermissionsData.error` is true */
    retry: PropTypes.func,
  }).isRequired,
  /**
   * Callback to handle selection change.
   * Handler is of shape: ({entity, checked}) => *
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Callback to set Selections.
   * Handler is of shape: (selectedNodes) => *
   */
  setSelection: PropTypes.func.isRequired,
  /**
   * IDs of selected entities.
   */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of entities we want to mark as disabled.
   */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether the component is multi selectable.
   */
  multiple: PropTypes.bool,
  /**
   * Whether we're currently in saving process.
   */
  isSaving: PropTypes.bool,
  /**
   * A handle method to add entities to cache.
   */
  addEntitiesToCache: PropTypes.func,
  /**
   * Additional props to pass to create form.
   */
  createFormProps: createFormPropsShape,
  /**
   * Additional props to pass to DataView.
   */
  dataViewProps: PropTypes.object,
  /**
   * Handler after successful creation of a new entity.
   * Used if `allowCreate` is true.
   */
  onCreateSuccess: PropTypes.func,
  /**
   * Whether create panel is currently open.
   */
  createPanelOpen: PropTypes.bool,
  /**
   * Set create panel open state.
   */
  setCreatePanelOpen: PropTypes.func,
  /**
   * Callback to handle an entity update.
   * Handler is of shape: (entity) => *
   */
  onUpdate: PropTypes.func,
  /**
   * Top bar info message markup
   */
  infoMessage: PropTypes.node,
  /**
   * Whether or not the plugin is disabled in entitlements.
   */
  isPluginDisabledInEntitlements: PropTypes.bool,
};

export default EntityPickerModalBody;
